import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { pdf } from '@react-pdf/renderer'
import { sortBy } from 'lodash';
import { v4 as uuid } from 'uuid';

import WarningIcon from '@material-ui/icons/ErrorOutline';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import { withStyles } from '@material-ui/core/styles';

import ShippingIcon from '../../../assets/icons/ShippingIcon';
import EditIcon from '../../../assets/icons/EditIcon';
import ChecklistIcon from '../../../assets/icons/ChecklistIcon';
import ConsignmentIcon from '../../../assets/icons/ConsignedSetIcon';
import KitIcon from '../../../assets/icons/KitIcon';
import ItemIcon from '../../../assets/icons/ItemIcon';
import AddIcon from '../../../assets/icons/AddIcon';

import CaseSetsTable from './CaseSetsTable';
import CaseSetsModal from './CaseSetsModal';
import CaseConsumablesModal from './CaseConsumablesModal';
import ShipSetsModal from './ShipSetsModal';
import TransferSetsModal from './TransferSetsModal';
import StatusActionButton from './ActionButton';
import ActionIconButton from '../../shared/ActionIconButton';
import CaseGuidanceModal from '../modals/CaseGuidanceModal';
import ShipNotesModal from '../modals/ShipNotesModal';
import DispatchPDF from './DispatchPDF';
import ShippingLabelsPDF from './ShippingLabelsPDF';
import DispatchModal, { dispatchDocumentTypes } from '../dispatch-modal/DispatchModal';
import ChecklistsPDF from '../checklists/ChecklistsPDF';
import PickingListPDF from '../checklists/PickingListPDF';
import UdiReferencePDF from '../checklists/UdiReferencePDF';
import AddKitsModal from '../sets/AddKitsModal';
import AddItemsModal from '../sets/AddItemsModal';
import { ConfirmationModal } from '../../shared/modal';

import { useAlert } from '../../../hooks';

import {
  updateSetsAllocation,
  bulkUpdateAllocations,
  getKitFiles,
  // exportDispatchDocument,
  addChecklist,
  deleteChecklist,
  simpleUpdateCase,
  deleteSetsAllocation,
  addNewKits,
  addNewItems,
  getCase,
  createActivity,
  deleteParentKit,
  sendSetsShippedNotification,
  onSetsAllocationAssigned,
  onSetsAllocationUnassigned,
} from '../../../actions/casesActions';
import { updateCaseAllocation } from '../../../actions/setsActions';
import { getSections } from '../../../actions/kitsActions';
import { getItemsByIds } from '../../../actions/itemsActions';

import { fromMomentToTimestampDay } from '../../../utils/date';
import { downloadFile } from '../../../utils/utils';

import {
  setAllocationStatuses, caseChecklistStatuses, kitTypeOptions,
  itemTypeOptions, kitVariantTypes, setPositionOptions,
  billOfMaterialItemTypes, consumableStatuses, notificationTypes,
} from '../../../constants/enums';
import routes from '../../../constants/routes';
import userRoles from '../../../constants/userRoles';

import '../cases.scss';

const { AVAILABLE, ASSIGNED, DISABLED, SHIPPED, TRANSFERRED, RETURNED, USAGE, CONFIRMED } = setAllocationStatuses;

const StyledMenuItem = withStyles((theme) => ({
  root: {
    paddingTop: '12px',
    paddingBottom: '12px',
  }
}))(MenuItem);

const CaseSets = (props) => {
  const {
    additional,
    kits = [],
    parentKits = [],
    additionalParentKits = [],
    activeCase,
    datePassed,
    onEditClick = () => {
    },
    editable,
    caseChecklists,
    labels = [],
    editingBy,
    dispatchDocuments = [],
    tenant,
    hospitals = [],
    surgeons = [],
    procedures = [],
    procedureOptions = [],
    caseActivity = [],
    users = [],
    title,
    userRole,
    setsAllocation,
    currentUser,
    isConsignment,
    connectedTenantId,
  } = props;

  const [items, setItems] = useState([]);
  const [, setItemsFetching] = useState(false);
  // const [caseChecklists, setCaseChecklists] = useState([]);

  const setsAllocationWithParents = useMemo(() => {
    if (!setsAllocation || !kits || !items) {
      return [];
    }
    let result = setsAllocation?.filter((allocation) => !allocation?.parentKitId)?.map((allocation) => {
      if (allocation.kit) {
        const kit = kits?.find((k) => k.id === allocation.kit);
        return { ...allocation, name: kit?.kitId || '', description: kit?.name || '' };
      } else {
        const item = items?.find((i) => i.id === allocation.itemId);
        return { ...allocation, name: item?.code || '', description: item?.checklistDescription || item?.description };
      }
    });

    result = sortBy(result, 'name');

    if (parentKits?.length && !additional) {
      parentKits?.forEach((kitId) => {
        const parent = kits?.find((k) => k.id === kitId);
        result.push({
          id: kitId,
          kit: kitId,
          name: parent?.kitId || '',
          description: parent?.name || '',
          isParent: true,
          quantity: 1
        });
        parent?.children?.forEach((c) => {
          const allocation = setsAllocation.find((a) => !a.additional && a.parentKitId === kitId && (a.kit === c.id || a.itemId === c.id));
          if (allocation) {
            if (allocation.kit) {
              const kit = kits?.find((k) => k.id === allocation.kit);
              result.push({ ...allocation, name: kit?.kitId || '', description: kit?.name || '' });
            } else {
              const item = items?.find((i) => i.id === allocation.itemId);
              result.push({
                ...allocation,
                name: item?.code || '',
                description: item?.checklistDescription || item?.description
              });
            }
          }
        });
      });
    }

    if (additionalParentKits?.length && !!additional) {
      additionalParentKits?.forEach((kitId) => {
        const parent = kits?.find((k) => k.id === kitId);
        result.push({
          id: kitId,
          kit: kitId,
          name: parent?.kitId || '',
          description: parent?.name || '',
          isParent: true,
          quantity: 1,
          additional: true
        });
        parent?.children?.forEach((c) => {
          const allocation = setsAllocation.find((a) => !!a.additional && a.parentKitId === kitId && (a.kit === c.id || a.itemId === c.id));
          if (allocation) {
            if (allocation.kit) {
              const kit = kits?.find((k) => k.id === allocation.kit);
              result.push({ ...allocation, name: kit?.kitId || '', description: kit?.name || '' });
            } else {
              const item = items?.find((i) => i.id === allocation.itemId);
              result.push({
                ...allocation,
                name: item?.code || '',
                description: item?.checklistDescription || item?.description
              });
            }
          }
        });
      });
    }

    return result;
  }, [setsAllocation, kits, items, parentKits, additionalParentKits, additional]);

  const { id } = activeCase;
  const noEquipmentToShip = additional ? (activeCase?.noEquipmentToShip && !setsAllocation?.length) : activeCase.noEquipmentToShip;
  const kitVariant = additional ? kitVariantTypes.loan : activeCase.kitVariant;

  const dispatch = useDispatch();
  const history = useHistory();

  const hospital = useMemo(() => hospitals?.find((h) => h.id === activeCase?.hospital), [hospitals, activeCase]);
  const surgeon = useMemo(() => surgeons?.find((h) => h.id === activeCase?.surgeon), [surgeons, activeCase]);
  const procedure = useMemo(() => procedures?.find((p) => p.id === activeCase?.procedure), [procedures, activeCase]);
  const procedureOption = useMemo(() => procedureOptions?.find((p) => p.id === activeCase?.procedureOption), [procedureOptions, activeCase]);

  const [kitsModalOpen, toggleKitsModal] = useState(false);
  const [itemsModalOpen, toggleItemsModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleClick = event => {
    if (userRole === userRoles.SALES_REP.name) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    let mounted = true;

    const fetchItems = async () => {
      try {
        setItemsFetching(true);
        const itemIds = Array.from(new Set(
          setsAllocation?.filter((allocation) => !!allocation.itemId).map((allocation) => allocation.itemId) || []
        ));
        const res = await dispatch(getItemsByIds(itemIds, connectedTenantId));

        if (mounted) {
          setItems(res || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) {
          setItemsFetching(false);
        }
      }
    };

    fetchItems().catch();

    return () => {
      mounted = false;
    };
  }, [dispatch, setsAllocation, connectedTenantId]);

  const getStatus = (set) => {
    if (!set.quarantined && !set.consigned && !set.caseAllocation) {
      // Batch Control
    } else {
      return set.quarantined ? (
        setPositionOptions.QUARANTINED.value
      ) : (
        set.consigned ? (
          setPositionOptions.CONSIGNED.value
        ) : (
          // set.caseAllocation && set.caseAllocation !== activeCase.id ? setPositionOptions.ALLOCATED.value : setPositionOptions.AVAILABLE.value
          set.caseAllocation ? setPositionOptions.ALLOCATED.value : setPositionOptions.AVAILABLE.value
        )
      );
    }
  };

  const sets = useMemo(() => {
    return props?.sets?.filter((s) => activeCase?.kits?.includes(s.kit) || activeCase?.additionalKits?.includes(s.kit))?.map((s) => ({
      ...s,
      status: getStatus(s)
    })) || [];
  }, [props.sets, items]);

  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(false);
  const [unassignLoading, setUnassignLoading] = useState(false);

  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [checkedSets, setCheckedSets] = useState([]);

  const [assignModalOpen, toggleAssignModal] = useState(false);
  const [consumablesModalOpen, toggleConsumablesModal] = useState(false);
  const [shipModalOpen, toggleShipModal] = useState(false);
  const [transferModalOpen, toggleTransferModal] = useState(null);
  const [dispatchModalOpen, toggleDispatchModal] = useState(false);
  const [deleteModalOpen, toggleDeleteModal] = useState(false);
  const [revertShippingModalOpen, toggleRevertShippingModal] = useState(false);

  const [documentsModalOpen, toggleDocumentsModal] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [documents, setDocuments] = useState([]);

  const [shipNotesModalOpen, toggleShipNotesModal] = useState(false);
  const [shippingNote, setShippingNote] = useState('');

  const [deleting, setDeleting] = useState(false);
  const [reverting, setReverting] = useState(false);

  const onSelectAllocation = (allocation) => {
    if (editingBy) {
      showAlert('warning', `${editingBy} is editing this case at the moment`);
    }

    if ([AVAILABLE.value, ASSIGNED.value, DISABLED.value].includes(allocation.status) && editable) {
      if (allocation.kit) {
        toggleAssignModal(true);
      } else {
        toggleConsumablesModal(true);
      }
      setSelectedAllocation(allocation);
    }
  };

  const handleCheckAll = () => {
    const list = setsAllocation.filter((item) => item.status !== DISABLED.value);
    const checked = checkedSets.length === list.length;
    const temp = [];

    if (!checked) {
      list.forEach((allocation) => {
        temp.push(allocation.id);
      });

      setCheckedSets(temp);
      return;
    }

    setCheckedSets(temp);
  };

  const handleCheck = (allocationId) => {
    const allocation = setsAllocation?.find((item) => item.id === allocationId);
    if (allocation.status === DISABLED.value) {
      return;
    }

    const checked = checkedSets.slice();

    if (checked.includes(allocationId)) {
      const index = checked.indexOf(allocationId);

      checked.splice(index, 1);
      setCheckedSets(checked);

      return;
    }

    checked.push(allocationId);
    setCheckedSets(checked);
  };

  const onAddSets = async (status, setIds, consumables) => {
    try {
      let data = { status };
      setLoading(true);

      if (status === ASSIGNED.value) {
        const kit = kits?.find((item) => item.id === selectedAllocation.kit);
        // await Promise.all(caseChecklists?.filter((item) => item.kitId === kit.kitId && !setIds.includes(item.id)).map((item) => dispatch(deleteChecklist(id, item.id))));
        if (setIds && setIds.length) {
          data.sets = setIds;
          await addChecklists(selectedAllocation, setIds, kit);
        } else {
          data.status = AVAILABLE.value;
          data.sets = [];
        }
      }

      if (status === DISABLED.value) {
        data.sets = [];
      }

      if (consumables && consumables.length) {
        data.consumables = consumables;
        data.status = ASSIGNED.value;
      }

      dispatch(updateSetsAllocation(id, selectedAllocation.id, data));
      setIds.forEach((setId) => dispatch(updateCaseAllocation(setId, activeCase.id)));
      dispatch(onSetsAllocationAssigned(activeCase.id, setIds));
      setSelectedAllocation(null);
      toggleAssignModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addChecklists = async (allocation, setIds, kit, caseId) => {
    let kitSections = await dispatch(getSections(allocation.kit, connectedTenantId));

    if (!kitSections) {
      const response = await dispatch(getSections(kit.id, connectedTenantId));
      kitSections = response || [];
    }

    let _items = [];
    for (const section of kitSections) {
      const itemIds = section.items?.filter((i) => i.type !== billOfMaterialItemTypes.NOTES.value).map((i) => i.id) || [];
      const newItems = await dispatch(getItemsByIds(itemIds, connectedTenantId));
      _items = [..._items, ...newItems];
    }

    if (kitSections && kitSections.length) {
      const filteredChecklists = caseId ? setIds : setIds.filter((setId) => !caseChecklists.find((item) => item.id === setId));
      const checklists = filteredChecklists.map((setId) => {
        const set = sets?.find((item) => item.id === setId);
        return {
          id: setId,
          allocationId: allocation?.id || '',
          additional: !!allocation.additional,
          consigned: kitVariant === kitVariantTypes.consignment,
          status: kitVariant === kitVariantTypes.consignment ? caseChecklistStatuses.COMPLETE.value : caseChecklistStatuses.INSPECT.value,
          kitId: kit.kitId || '',
          kitName: kit.name || '',
          setNumber: set.number || '',
          type: kitTypeOptions[kit?.type] ? kitTypeOptions[kit?.type].label : '',
          containerSize: set.containerSize || 1,
          quantity: allocation.quantity || 1,
          sections: kitSections?.map((section) => ({
            id: section.id || '',
            name: section.name || '',
            subtitle: section.subtitle || '',
            description: section.description || '',
            image: section.image || null,
            printOnNewPage: !!section.printOnNewPage,
            items: section.items?.map((item) => {
              if (item.type === billOfMaterialItemTypes.NOTES.value) {
                return item;
              } else {
                const _item = _items?.find((i) => i.id === item.id);
                return {
                  id: item.id || '',
                  quantity: item.quantity || 0,
                  outbound: kitVariant === kitVariantTypes.consignment && set?.itemsQuantity?.[item.id] !== undefined ? set?.itemsQuantity?.[item.id] : (item.quantity || 0),
                  ref: item.ref || '',
                  description: _item?.checklistDescription || _item?.description || '',
                  code: _item?.code || '',
                  type: itemTypeOptions[_item?.type] ? itemTypeOptions[_item?.type].label : '',
                  image: _item?.image || null,
                  imageUrl: _item?.imageUrl || '',
                  hospitalIn: 0,
                  hospitalOut: 0,
                  returned: 0,
                };
              }
            })?.filter((item) => item.type === billOfMaterialItemTypes.NOTES.value || (item && item.id && item.code)),
          })),
        };
      });

      await Promise.all(checklists.map((checklist) => dispatch(addChecklist(caseId || id, checklist))));
    }
  };

  const onAddConsumables = async (consumables = []) => {
    try {
      setLoading(true);
      const data = { status: AVAILABLE.value };

      if (consumables && consumables.length) {
        data.status = ASSIGNED.value;
        data.consumables = consumables?.map((c) => ({
          id: uuid(),
          label: c,
          status: consumableStatuses.RETURNED.value,
        }));
      }

      await dispatch(updateSetsAllocation(id, selectedAllocation.id, data));
      setSelectedAllocation(null);
      toggleConsumablesModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onUnassignClick = async () => {
    try {
      setUnassignLoading(true);
      const data = { status: AVAILABLE.value, consumables: [], sets: [] };
      await dispatch(updateSetsAllocation(id, selectedAllocation.id, data));

      if (selectedAllocation.kit) {
        const kit = kits?.find((k) => k.id === selectedAllocation.kit);
        const checklists = caseChecklists?.filter((c) => c.kitId === kit?.kitId && c.kitName === kit?.name && !!c.additional === !!selectedAllocation.additional);
        if (checklists?.length) {
          for (const checklist of checklists) {
            await dispatch(deleteChecklist(id, checklist.id));
          }
        }
      }

      selectedAllocation?.sets?.forEach((setId) => {
        dispatch(updateCaseAllocation(setId, null));
      });

      dispatch(onSetsAllocationUnassigned(activeCase.id, selectedAllocation?.sets));

      toggleConsumablesModal(false);
      toggleAssignModal(false);
      setSelectedAllocation(null);
    } catch (err) {
      console.error(err);
    } finally {
      setUnassignLoading(false);
    }
  };

  const onShipButtonClick = () => {
    if (!editable) {
      return;
    }

    const allocations = checkedSets.map((id) => setsAllocation?.find((item) => item.id === id));
    if ((!!checkedSets.length && allocations.every((allocation) => allocation.status === ASSIGNED.value)) || !checkedSets.length) {
      toggleShipModal(true);
    }
  };

  const onShipSets = async (formObj, sets) => {
    try {
      setLoading(true);

      const data = sets.map((allocationId) => {
        const allocation = setsAllocation?.find((item) => item.id === allocationId);
        return {
          id: allocation.id,
          status: kitVariant === kitVariantTypes.consignment ? CONFIRMED.value : SHIPPED.value,
          trackingNumber: formObj.trackingNumber || '',
          shippingDate: fromMomentToTimestampDay(formObj.shippingDate ? formObj.shippingDate : moment()),
          shippingNote: formObj.shippingNote || '',
          freightCost: formObj.freightCost || 0,
        };
      });

      await dispatch(bulkUpdateAllocations(id, data, items, kitVariant === kitVariantTypes.consignment));

      const warningKits = activeCase?.warningKits ? activeCase?.warningKits?.filter((kit) => !sets?.includes(kit)) : [];
      dispatch(simpleUpdateCase(activeCase?.id, { warningKits }));
      dispatch(getCase(activeCase?.id));

      setCheckedSets([]);
      toggleShipModal(false);
      // updateInstance();

      // Send notifications
      if (kitVariant !== kitVariantTypes.consignment) {
        dispatch(sendSetsShippedNotification(activeCase.id));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Transfer sets

  const onTransferButtonClick = () => {
    if (!editable) {
      return;
    }

    const allocations = checkedSets.map((id) => setsAllocation?.find((item) => item.id === id));
    if (allocations.every((allocation) => allocation.status === SHIPPED.value || allocation.status === CONFIRMED.value)) {
      toggleTransferModal(allocations);
    }
  };

  const onTransferSets = async (allocations, transferTo) => {
    try {
      setLoading(true);

      const data = {
        status: TRANSFERRED.value,
        transferTo,
      };

      for (const allocation of allocations) {
        dispatch(updateSetsAllocation(id, allocation.id, data));
        const kit = kits?.find((item) => item.id === allocation.kit);
        await addChecklists(allocation, allocation.sets, kit, transferTo);
      }
      setCheckedSets([]);
      toggleTransferModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSets = useMemo(() => {
    if (!sets || !selectedAllocation) {
      return [];
    }

    return sets.filter((set) => selectedAllocation.kit === set.kit);
  }, [sets, selectedAllocation]);

  const shippingButtonDisabled = () => {
    if (!editable) {
      return true;
    }

    const allocations = checkedSets.map((id) => setsAllocation?.find((item) => item.id === id));

    if (
      setsAllocation
        ?.filter((allocation) => allocation.status !== DISABLED.value)
        ?.every((allocation) => [SHIPPED.value, RETURNED.value, USAGE.value, CONFIRMED.value].includes(allocation.status))
    ) {
      return true;
    }

    if (
      setsAllocation
        ?.filter((allocation) => allocation.status !== DISABLED.value)
        ?.every((allocation) => allocation.status === AVAILABLE.value)
    ) {
      return true;
    }

    if (!checkedSets.length) {
      return false;
    }

    return (
      (!!checkedSets.length && allocations.some((allocation) => allocation?.status !== ASSIGNED.value)) || (
        !allocations.some((item) => (
          item.status === ASSIGNED.value && (
            !item?.sets?.length || item?.sets?.every((setId) => {
              const checklist = caseChecklists.find((item) => item.id === setId);
              return !checklist || (checklist && [caseChecklistStatuses.COMPLETE.value, caseChecklistStatuses.INCOMPLETE.value].includes(checklist?.status));
            })
          )
        ))
      )
    );
  }

  const returnButtonDisabled = () => {
    if (!editable) {
      return true;
    }

    if (!checkedSets.length) {
      return false;
    }

    const allocations = checkedSets.map((id) => setsAllocation.find((item) => item.id === id));

    return allocations
      .filter((allocation) => allocation?.status !== DISABLED.value)
      .some((allocation) => allocation?.status !== SHIPPED.value || allocation.status !== CONFIRMED.value);
  }

  const dispatchButtonDisabled = () => {
    return userRole === userRoles.SALES_REP.name;

    // if (userRole === userRoles.SALES_REP.name) {
    //   return true;
    // }

    // return (
    //   setsAllocation?.every((allocation) => !allocation.trackingNumber)
    //   && caseChecklists?.every((checklist) => ![caseChecklistStatuses.COMPLETE.value, caseChecklistStatuses.INCOMPLETE.value]?.includes(checklist?.status))
    // );
  };

  // const usageButtonDisabled = () => {
  //   if (!editable || checkedSets.length !== 1) {
  //     return true;
  //   }
  //
  //   const allocation = setsAllocation?.find((item) => item.id === checkedSets[0]);
  //   return allocation?.status !== SHIPPED.value || !!allocation?.sets?.length || !allocation?.consumables?.length;
  // }

  const transferButtonDisabled = () => {
    if (!editable || !checkedSets.length) {
      return true;
    }

    return checkedSets?.every((checkedSet) => {
      const allocation = setsAllocation?.find((item) => item.id === checkedSet);
      return ![SHIPPED.value, CONFIRMED.value]?.includes(allocation?.status) || !allocation.sets || !allocation.sets.length
    });
  }

  const onDocumentsModalOpen = async (kitId) => {
    if (userRole === userRoles.SALES_REP.name) {
      return;
    }

    setFetching(true);
    toggleDocumentsModal(true);

    try {
      const files = kitId ? await dispatch(getKitFiles(kitId)) : [];
      setDocuments(files);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const onNotesModalOpen = async (kit) => {
    setShippingNote(kit.shippingNote);
    toggleShipNotesModal(true);
  };

  const onCloseShipNotesModal = () => {
    toggleShipNotesModal(false);
    setTimeout(() => setShippingNote(''), 500);
  }

  const onExportClick = async (exportData) => {
    const {
      selected = [],
      dispatchNote = '',
      shippingNote = '',
      selectedChecklistKits = [],
      selectedChecklistItems = [],
      selectedPickingListKits = [],
      selectedPickingListItems = [],
      // documents = [],
    } = exportData;

    try {
      setLoading(true);
      if (selected.includes(dispatchDocumentTypes.DISPATCH_DOCUMENT)) {
        await downloadDispatchDoc(dispatchNote);
      }
      if (selected.includes(dispatchDocumentTypes.CHECKLISTS)) {
        await downloadChecklistsDoc(selectedChecklistKits, selectedChecklistItems);
      }
      if (selected.includes(dispatchDocumentTypes.UDI_REFERENCE_DOCUMENT)) {
        await downloadUdiReferenceDoc(selectedChecklistKits, selectedChecklistItems);
      }
      if (selected.includes(dispatchDocumentTypes.PICKING_LIST)) {
        await downloadPickingListDoc(selectedPickingListKits, selectedPickingListItems);
      }
      if (selected.includes(dispatchDocumentTypes.SHIPPING_LABELS)) {
        await downloadShippingLabelsDoc(shippingNote);
      }
      // if (checklistsAdditional) {
      //   await downloadChecklistsDoc(true);
      // }
      // for (const doc of documents) {
      //   await dispatch(exportDispatchDocument(doc.path, doc.fileName));
      // }
      toggleDispatchModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const downloadDispatchDoc = async (note) => {
    const user = users.find((u) => u.uid === activeCase?.createdBy);
    const blob = await pdf(
      <DispatchPDF
        tenant={tenant}
        activeCase={activeCase}
        hospital={hospital}
        surgeon={surgeon}
        procedure={procedure}
        procedureOption={procedureOption}
        deliveryAddress={activeCase?.deliveryAddress}
        setsAllocation={setsAllocation}
        kits={kits}
        items={items}
        sets={sets}
        note={note}
        user={user}
      />
    ).toBlob();
    downloadFile(blob, `${activeCase.id} – Dispatch.pdf`);
  };

  const downloadShippingLabelsDoc = async (note) => {
    const blob = await pdf(
      <ShippingLabelsPDF
        tenant={tenant}
        activeCase={activeCase}
        hospital={hospital}
        surgeon={surgeon}
        procedure={procedure}
        procedureOption={procedureOption}
        deliveryAddress={activeCase?.deliveryAddress}
        setsAllocation={setsAllocation}
        kits={kits}
        items={items}
        sets={sets}
        note={note}
        user={currentUser}
      />
    ).toBlob();
    downloadFile(blob, `${activeCase.id} – Shipping Labels.pdf`);
  };

  const getChecklistExportData = async (selectedKits, selectedItems, includeItemDetails = false) => {
    const standaloneItems = setsAllocation?.filter((allocation) => !!allocation.itemId && selectedItems.includes(allocation.id))?.map((allocation) => {
      const item = items?.find((i) => i.id === allocation.itemId);
      return {
        ...allocation,
        name: item?.code || '',
        description: item?.checklistDescription || item?.description,
        rebateCode: item?.rebateCode || '',
        gtin: item?.gtin || '',
        type: item?.type || '',
      };
    });
    const loanKits = kits.filter((k) => !k.isConsignment).map((k) => k.id);

    const checklists = caseChecklists?.filter((checklist) => {
      const allocation = setsAllocation.find((a) => a.id === checklist.allocationId);
      return allocation && selectedKits?.includes(allocation?.id) && loanKits.includes(allocation?.kit) && [caseChecklistStatuses.COMPLETE.value, caseChecklistStatuses.INCOMPLETE.value].includes(checklist.status);
    });

    const checklistItemIds = Array.from(new Set(checklists?.reduce((result, checklist) => {
      checklist.sections?.forEach((section) => {
        section.items?.forEach((item) => {
          if (item?.type !== billOfMaterialItemTypes.NOTES.value && item?.id) {
            result.push(item.id);
          }
        });
      });
      return result;
    }, []) || []));

    const checklistItems = includeItemDetails && checklistItemIds.length
      ? await dispatch(getItemsByIds(checklistItemIds, connectedTenantId))
      : [];

    return {
      checklists,
      checklistItems,
      standaloneItems,
    };
  };

  const downloadChecklistsDoc = async (selectedKits, selectedItems) => {
    const {
      checklists,
      standaloneItems,
    } = await getChecklistExportData(selectedKits, selectedItems);

    const blob = await pdf(
      <ChecklistsPDF
        activeCase={activeCase}
        tenant={tenant}
        hospital={hospital}
        surgeon={surgeon}
        kits={kits}
        sets={sets}
        checklists={checklists}
        caseActivity={caseActivity}
        users={users}
        withItems={!!standaloneItems.length}
        items={standaloneItems}
      />
    ).toBlob();
    downloadFile(blob, `${activeCase.id} – Checklists.pdf`);
  };

  const downloadUdiReferenceDoc = async (selectedKits, selectedItems) => {
    const {
      checklists,
      checklistItems,
      standaloneItems,
    } = await getChecklistExportData(selectedKits, selectedItems, true);

    const blob = await pdf(
      <UdiReferencePDF
        activeCase={activeCase}
        tenant={tenant}
        hospital={hospital}
        surgeon={surgeon}
        kits={kits}
        checklists={checklists}
        caseActivity={caseActivity}
        users={users}
        withItems={!!standaloneItems.length}
        items={standaloneItems}
        itemDetails={checklistItems}
      />
    ).toBlob();
    downloadFile(blob, `${activeCase.id} – UDI Reference Document.pdf`);
  };

  const downloadPickingListDoc = async (selectedKits, selectedItems) => {
    const docKits = setsAllocation?.filter((allocation) => !!allocation.kit && selectedKits.includes(allocation.id))?.map((allocation) => {
      const _kit = kits?.find((k) => k.id === allocation.kit);
      const _sets = allocation.sets.map((setId) => {
        const _set = sets.find((s) => s.id === setId);
        return _set ? _set.number : '';
      });
      return {
        ...allocation,
        kitId: _kit?.kitId || '',
        name: _kit?.name || '',
        status: allocation.status === setAllocationStatuses.AVAILABLE.value ? 'ASSIGN' : allocation.status,
        sets: _sets.join(', '),
      };
    });
    const docItems = setsAllocation?.filter((allocation) => !!allocation.itemId && selectedItems.includes(allocation.id))?.map((allocation) => {
      const _item = items?.find((i) => i.id === allocation.itemId);
      return {
        ...allocation,
        code: _item?.code || '',
        description: _item?.checklistDescription || _item?.description,
        status: allocation.status === setAllocationStatuses.AVAILABLE.value ? 'ASSIGN' : allocation.status,
        serial: allocation?.consumables ? allocation.consumables.map((c) => c.label || '')?.join(', ') : '',
      };
    });

    const blob = await pdf(
      <PickingListPDF
        activeCase={activeCase}
        tenant={tenant}
        hospital={hospital}
        surgeon={surgeon}
        kits={sortBy(docKits, 'kitId')}
        items={sortBy(docItems, 'code')}
        user={currentUser}
      />
    ).toBlob();
    downloadFile(blob, `${activeCase.id} – Picking List.pdf`);
  };

  const onDeleteClick = (allocation) => {
    setSelectedAllocation(allocation);
    toggleDeleteModal(true);
  };

  const onDeleteAllocation = async () => {
    if (selectedAllocation) {
      try {
        setDeleting(true);

        if (selectedAllocation?.isParent) {
          const children = setsAllocationWithParents?.filter((allocation) => allocation?.parentKitId === selectedAllocation.id && !!selectedAllocation.additional === !!allocation.additional);
          const kits = [...activeCase?.kits] || [];
          const items = [...activeCase?.items] || [];
          const additionalKits = activeCase?.additionalKits ? [...activeCase?.additionalKits] : [];
          const additionalItems = activeCase?.additionalItems ? [...activeCase?.additionalItems] : [];

          for (const allocation of children) {
            await dispatch(deleteSetsAllocation(activeCase, allocation));
          }
          await dispatch(deleteParentKit(activeCase, selectedAllocation.id, selectedAllocation.additional));

          if (selectedAllocation.additional) {
            const doc = {
              additionalKits: additionalKits?.filter((kitId) => !children?.map((c) => c.kit)?.includes(kitId)),
              additionalItems: additionalItems?.filter((itemId) => !children?.map((c) => c.itemId)?.includes(itemId)),
            };
            await dispatch(simpleUpdateCase(activeCase.id, doc));
          } else {
            const doc = {
              kits: kits?.filter((kitId) => !children?.map((c) => c.kit)?.includes(kitId)),
              items: items?.filter((itemId) => !children?.map((c) => c.itemId)?.includes(itemId)),
            };
            await dispatch(simpleUpdateCase(activeCase.id, doc));
          }
        } else {
          await dispatch(deleteSetsAllocation(activeCase, selectedAllocation));
        }

        await dispatch(getCase(activeCase?.id));
        toggleDeleteModal(false);
        setSelectedAllocation(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleting(false);
      }
    }
  };

  const onAddNewKits = async (kits) => {
    try {
      handleClose();
      setCreating(true);
      await dispatch(addNewKits(activeCase, kits, additional));
      await dispatch(getCase(activeCase.id));
      toggleKitsModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const onAddNewItems = async (items) => {
    try {
      handleClose();
      setCreating(true);
      await dispatch(addNewItems(activeCase, items, additional));
      await dispatch(getCase(activeCase.id));
      toggleItemsModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const onRevertShippingClick = async (allocation) => {
    setSelectedAllocation(allocation);
    toggleRevertShippingModal(true);
  };

  const onRevertShipping = async () => {
    if (selectedAllocation) {
      try {
        setReverting(true);
        const doc = { status: ASSIGNED.value, shippingNote: '', shippingDate: null, trackingNumber: '', freightCost: null};
        await dispatch(updateSetsAllocation(activeCase.id, selectedAllocation.id, doc));

        let name = '';

        if (selectedAllocation.kit) {
          const kit = kits?.find((k) => k.id === selectedAllocation.kit);
          name = `${kit.kitId}, ${kit.name}`;
        }
        if (selectedAllocation.itemId) {
          const item = items?.find((i) => i.id === selectedAllocation.itemId);
          name = `${item.code}, ${item.description}`;
        }

        const activity = {
          type: notificationTypes.shippingReversed,
          title: 'Shipping Reversed',
          payload: `Shipping has been reversed for the following: ${name}`,
        };
        await dispatch(createActivity(activeCase.id, activity));
        toggleRevertShippingModal(false);
        setSelectedAllocation(null);
      } catch (err) {
        console.error(err);
      } finally {
        setReverting(false);
      }
    }
  };

  const showReturnButton = useMemo(() => (
    setsAllocation?.some((item) => item.status === SHIPPED.value || item.status === CONFIRMED.value) || datePassed
  ), [setsAllocation, datePassed]);

  const availableToShipSets = useMemo(() => {
    return (
      setsAllocation?.filter((item) => (
        item.status === ASSIGNED.value && (activeCase.kitVariant === kitVariantTypes.loan || !!item.additional === !!additional) && (
          !item?.sets?.length || item?.sets?.every((setId) => {
            const checklist = caseChecklists.find((item) => item.id === setId);
            return !checklist || (checklist && [caseChecklistStatuses.COMPLETE.value, caseChecklistStatuses.INCOMPLETE.value].includes(checklist.status));
          })
        )
      ))
    );
  }, [setsAllocation, caseChecklists, additional, activeCase.kitVariant]);

  const isWarning = useMemo(() => activeCase?.warningKits && !!activeCase?.warningKits?.length, [activeCase]);
  const iconColor = 'var(--app-text)';

  return (
    <div className='case-kits__container'>
      {/*{itemsFetching && <LoadScreen />}*/}
      <div className='case-sets__container-title'>
        <div>
          <div className='d-flex'>
            {kitVariant && kitVariant === kitVariantTypes.consignment ? (
              <ConsignmentIcon color={iconColor} />
            ) : (
              <ShippingIcon color={iconColor} />
            )}
            <div className='font-size-bg font-bold m-l-md'>
              {title}
            </div>
          </div>
          {isWarning && (
            <div className='d-flex flex-end'>
              <span className='font-bold' style={{ color: setAllocationStatuses.QUARANTINED.color }}>
                Set Availability Warning
              </span>
              <WarningIcon style={{ color: setAllocationStatuses.QUARANTINED.color, marginLeft: 4 }} />
            </div>
          )}
        </div>

        <div className='case-sets-actions__container'>
          {editable && !(activeCase.kitVariant === kitVariantTypes.loan && !!activeCase.kitPreference && !additional) && (
            <>
              <ActionIconButton
                disabled={userRole === userRoles.SALES_REP.name}
                onClick={handleClick}
                color='blue'
                tooltip='Add'
              >
                <AddIcon />
                {/*Add*/}
              </ActionIconButton>
              <Menu
                id='long-menu'
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    maxHeight: 200,
                    width: 200,
                  },
                  elevation: 3,
                }}
              >
                <StyledMenuItem
                  onClick={() => {
                    if (userRole !== userRoles.SALES_REP.name) {
                      toggleKitsModal(true);
                      handleClose();
                    }
                  }}
                >
                  <KitIcon color={iconColor} />
                  <span className='m-l-md'>Add Kits</span>
                </StyledMenuItem>
                <StyledMenuItem
                  onClick={() => {
                    if (userRole !== userRoles.SALES_REP.name) {
                      toggleItemsModal(true);
                      handleClose();
                    }
                  }}
                >
                  <ItemIcon color={iconColor} />
                  <span className='m-l-md'>Add Items</span>
                </StyledMenuItem>
              </Menu>
            </>
          )}

          {editable && (
            <ActionIconButton
              disabled={userRole === userRoles.SALES_REP.name}
              onClick={onEditClick}
              color='blue'
              tooltip='Edit'
            >
              <EditIcon />
              {/*Edit*/}
            </ActionIconButton>
          )}

          {(!datePassed || isConsignment) && !(activeCase.kitPreference && additional) && !(activeCase.kitVariant === kitVariantTypes.loan && additional) && (
            <StatusActionButton
              status={kitVariant === kitVariantTypes.consignment ? CONFIRMED.value : SHIPPED.value}
              onClick={onShipButtonClick}
              disabled={shippingButtonDisabled()}
              kitVariant={kitVariant}
            />
          )}

          {!noEquipmentToShip && (
            <>
              {showReturnButton && (
                <>
                  <StatusActionButton
                    status={kitVariant === kitVariantTypes.consignment ? USAGE.value : RETURNED.value}
                    onClick={() => {
                      if (userRole !== userRoles.SALES_REP.name) {
                        history.push(`${routes.CASES}/${id}${additional ? routes.RETURN_ADDITIONAL_SETS : routes.RETURN_SETS}`);
                      }
                    }}
                    disabled={returnButtonDisabled()}
                  />
                  {kitVariant !== kitVariantTypes.consignment && (
                    <StatusActionButton
                      status={setAllocationStatuses.TRANSFERRED.value}
                      onClick={onTransferButtonClick}
                      disabled={transferButtonDisabled()}
                    />
                  )}
                </>
              )}
            </>
          )}

          {((!noEquipmentToShip && kitVariant !== kitVariantTypes.consignment && !(activeCase.kitPreference && additional) && !(activeCase.kitVariant === kitVariantTypes.loan && additional)) || (activeCase.kitVariant === kitVariantTypes.loan && !additional && !!setsAllocation?.filter((set) => !!set.additional)?.length)) && (
            <StatusActionButton
              status={setAllocationStatuses.DISPATCH.value}
              onClick={() => {
                if (userRole !== userRoles.SALES_REP.name) {
                  toggleDispatchModal(true);
                }
              }}
              disabled={dispatchButtonDisabled()}
            />
          )}

          {caseChecklists && !!caseChecklists?.filter((item) => !item.consigned)?.length && kitVariant !== kitVariantTypes.consignment && !(activeCase.kitPreference && additional) && !(activeCase.kitVariant === kitVariantTypes.loan && additional) && (
            <ActionIconButton
              disabled={userRole === userRoles.SALES_REP.name}
              onClick={() => {
                if (userRole !== userRoles.SALES_REP.name) {
                  history.push(`${routes.CASES}/${id}${routes.CHECKLISTS}`);
                }
              }}
              color='orange'
              tooltip='Checklists'
            >
              <ChecklistIcon />
              {/*Checklists*/}
            </ActionIconButton>
          )}
        </div>
      </div>

      {noEquipmentToShip ? (
        <div className='font-size-bg secondary font-bold uppercase text-center p-lg m-lg'>
          No equipment to ship
        </div>
      ) : (
        <CaseSetsTable
          caseId={activeCase.id}
          setsAllocation={setsAllocationWithParents?.filter((allocation) => additional || !allocation.additional)}
          checkedSets={checkedSets}
          onSelectAllocation={onSelectAllocation}
          selectedAllocationId={selectedAllocation ? selectedAllocation.id : null}
          handleCheck={handleCheck}
          handleCheckAll={handleCheckAll}
          kits={kits}
          sets={sets}
          onButtonClick={onDocumentsModalOpen}
          onKitNotesClick={onNotesModalOpen}
          caseChecklists={caseChecklists}
          items={items}
          kitVariant={kitVariant}
          warningKits={activeCase?.warningKits || []}
          additional={additional}
          onDeleteClick={onDeleteClick}
          onRevertShipping={onRevertShippingClick}
          userRole={userRole}
        />
      )}

      <CaseSetsModal
        open={assignModalOpen}
        onClose={() => {
          toggleAssignModal(false);
          setSelectedAllocation(null)
        }}
        sets={selectedAllocation ? filteredSets : []}
        selected={selectedAllocation ? selectedAllocation.sets : []}
        caseId={id}
        onSubmit={onAddSets}
        loading={loading}
        unassignLoading={unassignLoading}
        kitId={selectedAllocation ? selectedAllocation.kit : null}
        kits={kits}
        status={selectedAllocation ? selectedAllocation.status : null}
        datePassed={datePassed}
        initialConsumables={selectedAllocation ? selectedAllocation.consumables : []}
        quantity={selectedAllocation ? selectedAllocation.quantity : 1}
        labels={labels}
        kitVariant={kitVariant}
        onUnassignClick={onUnassignClick}
        caseHospital={activeCase?.hospital}
        items={items}
        additional={additional}
        isConsignment={isConsignment}
      />

      <CaseConsumablesModal
        open={consumablesModalOpen}
        onClose={() => {
          toggleConsumablesModal(false);
          setSelectedAllocation(null)
        }}
        onSubmit={onAddConsumables}
        loading={loading}
        unassignLoading={unassignLoading}
        itemId={selectedAllocation ? selectedAllocation.itemId : null}
        items={items}
        initialConsumables={selectedAllocation ? selectedAllocation?.consumables?.map((c) => c.label) : []}
        quantity={selectedAllocation ? selectedAllocation.quantity : 1}
        onUnassignClick={onUnassignClick}
      />

      <ShipSetsModal
        open={shipModalOpen}
        onClose={() => toggleShipModal(false)}
        onSubmit={onShipSets}
        loading={loading}
        availableSets={availableToShipSets}
        checkedSets={checkedSets}
        kits={kits}
        sets={sets}
        items={items}
        kitVariant={kitVariant}
      />

      <TransferSetsModal
        open={!!transferModalOpen}
        onClose={() => toggleTransferModal(null)}
        allocations={transferModalOpen}
        onSubmit={onTransferSets}
        loading={loading}
        caseId={id}
      />

      <CaseGuidanceModal
        open={documentsModalOpen}
        onClose={() => toggleDocumentsModal(false)}
        files={documents}
        fetching={fetching}
        title='Kit Documents'
      />

      <ShipNotesModal
        open={shipNotesModalOpen}
        onClose={onCloseShipNotesModal}
        note={shippingNote}
        title={kitVariant === kitVariantTypes.consignment ? 'Notes' : 'Shipping Notes'}
      />

      <DispatchModal
        open={dispatchModalOpen}
        onClose={() => toggleDispatchModal(false)}
        onSubmit={onExportClick}
        loading={loading}
        dispatchDocuments={dispatchDocuments}
        tenantColor={tenant.colorPrimary}
        kitsList={kits}
        itemsList={items}
        setsAllocation={setsAllocation.filter((allocation) => [AVAILABLE, ASSIGNED, SHIPPED, RETURNED, USAGE].map((a) => a.value).includes(allocation.status))}
        caseChecklists={caseChecklists}
      />

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          toggleDeleteModal(false);
          setSelectedAllocation(null);
        }}
        onSubmit={onDeleteAllocation}
        title='Remove from Shipping List'
        text={selectedAllocation?.isParent ? 'Are you sure you want to remove all kits & items related to this parent kit from the shipping list?' : 'Are you sure you want to remove this from the shipping list?'}
        submitText='Remove'
        loading={deleting}
      />

      <ConfirmationModal
        open={revertShippingModalOpen}
        onClose={() => {
          toggleRevertShippingModal(false);
          setSelectedAllocation(null);
        }}
        onSubmit={onRevertShipping}
        title='Reverse shipping status?'
        text='Continue to set this line back to an Assigned status and permanently delete shipping information!'
        submitText='Confirm'
        loading={reverting}
      />

      <AddKitsModal
        open={kitsModalOpen}
        onClose={() => toggleKitsModal(false)}
        options={kits?.filter((k) => {
          if (additional) {
            return !!k.active && !activeCase.additionalKits?.includes(k.id) && !k.isConsignment;
          }
          return !!k.active && !activeCase.additionalKits?.includes(k.id);
        })}
        onSubmit={onAddNewKits}
        loading={creating}
      />

      <AddItemsModal
        open={itemsModalOpen}
        onClose={() => toggleItemsModal(false)}
        disabledItems={activeCase.additionalItems}
        onSubmit={onAddNewItems}
        loading={creating}
      />
    </div>
  );
};

export default CaseSets;
